import * as tslib_1 from "tslib";
import { ErrorHandler } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from '../../auth/auth/auth.service';
import { UserInfo } from '../models/user-info.model';
import { environment } from '../../../environments/environment';
import { AuthEventType } from '../../auth/models/auth-event.model';
import { FavoriteAirport } from '../models/user-data/favorite-airport.model';
import { Review } from '../models/review.model';
import { ReviewSummary } from '../models/review-summary.model';
import { UserEcho } from '../models/user-echo.model';
import { Photo } from '../models/photo.model';
import { FavoriteEvent } from '../models/user-data/favorite-event.model';
var UserService = /** @class */ (function () {
    function UserService(_http, _auth, _errorHandler) {
        var _this = this;
        this._http = _http;
        this._auth = _auth;
        this._errorHandler = _errorHandler;
        /**
         * User info stream
         */
        this.userInfo = new BehaviorSubject(null);
        this._auth.authStream.subscribe(function (e) { return _this._onAuthEvent(e); });
    }
    UserService.prototype._onAuthEvent = function (e) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _a;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!e) {
                            return [2 /*return*/];
                        }
                        _a = e.type;
                        switch (_a) {
                            case AuthEventType.SignIn: return [3 /*break*/, 1];
                            case AuthEventType.InvalidSignIn: return [3 /*break*/, 3];
                            case AuthEventType.SignOut: return [3 /*break*/, 3];
                        }
                        return [3 /*break*/, 4];
                    case 1: return [4 /*yield*/, this.getUserInfo()];
                    case 2:
                        _b.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        this._clearUserInfo();
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    UserService.prototype._clearUserInfo = function () {
        var userInfo = this.userInfo.getValue();
        if (userInfo !== null) {
            this.userInfo.next(null);
        }
    };
    /**
     * Gets the authenticated user's information. Note this method requires that the user is authenticated.
     * @returns {Promise<UserInfo>} User info
     */
    UserService.prototype.getUserInfo = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, userInfo, e_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser) {
                            return [2 /*return*/, null];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/customer").toPromise()];
                    case 2:
                        response = _a.sent();
                        userInfo = UserInfo.parse(response);
                        this.userInfo.next(userInfo);
                        return [2 /*return*/, userInfo];
                    case 3:
                        e_1 = _a.sent();
                        this._errorHandler.handleError(e_1);
                        throw e_1;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Adds the given airport identifier to the user's list of favorite airports. Requires that the user is authenticated,
     * and that userInfo is loaded.
     * @param {string} identifier Airport identifier to add.
     * @returns {Promise<FavoriteAirport>} Favorite airport record.
     */
    UserService.prototype.addFavoriteAirport = function (identifier) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, existingFavorite, response, favoriteAirport;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
                        }
                        if (!identifier) {
                            throw new Error('Missing identifier');
                        }
                        existingFavorite = userInfo.favoriteAirports.find(function (f) { return f.identifier === identifier; });
                        if (existingFavorite) {
                            return [2 /*return*/, existingFavorite];
                        }
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/customer/favorites/" + identifier, null).toPromise()];
                    case 1:
                        response = _a.sent();
                        favoriteAirport = FavoriteAirport.parse(response);
                        // Update userInfo
                        userInfo.favoriteAirports.push(favoriteAirport);
                        this.userInfo.next(userInfo);
                        return [2 /*return*/, favoriteAirport];
                }
            });
        });
    };
    /**
     * Removes the given airport identifier from the user's list of favorite airports. Requires that the user is
     * authenticated, and that userInfo is loaded.
     * @param {string} identifier Airport identifier to remove.
     * @returns {Promise<void>}
     */
    UserService.prototype.removeFavoriteAirport = function (identifier) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, index, existingFavorite;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
                        }
                        if (!identifier) {
                            throw new Error('Missing identifier');
                        }
                        index = userInfo.favoriteAirports.findIndex(function (f) { return f.identifier === identifier; });
                        existingFavorite = null;
                        if (index >= 0) {
                            existingFavorite = userInfo.favoriteAirports[index];
                        }
                        else {
                            return [2 /*return*/];
                        }
                        // Remove the favorite
                        return [4 /*yield*/, this._http.delete(environment.airportsApiUrl + "/customer/favorites/" + existingFavorite.userDataId).toPromise()];
                    case 1:
                        // Remove the favorite
                        _a.sent();
                        // Update userInfo
                        userInfo.favoriteAirports.splice(index, 1);
                        this.userInfo.next(userInfo);
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * inserts specified entity via api
     * @param {Review} review object containing rating and review information to save to ratings for this entity.
     * @param {IEntity} entity object containing summary information about this entity (airport or business).
     * @return {Promise<Review>} Resulting review record from API.
     */
    UserService.prototype.postReview = function (review, entity) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, response, BODY, e_2;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to save a rating.');
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 9, , 10]);
                        response = void 0;
                        BODY = this._getReviewJSON(review);
                        if (!(entity.type === 'Airport')) return [3 /*break*/, 3];
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/airports/" + entity.id + "/review", BODY).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [3 /*break*/, 8];
                    case 3:
                        if (!(entity.type === 'Business')) return [3 /*break*/, 5];
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/businesses/" + entity.id + "/review", BODY).toPromise()];
                    case 4:
                        response = _a.sent();
                        return [3 /*break*/, 8];
                    case 5:
                        if (!(entity.type === 'Flying Club')) return [3 /*break*/, 7];
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/businesses/" + entity.id + "/review", BODY).toPromise()];
                    case 6:
                        response = _a.sent();
                        return [3 /*break*/, 8];
                    case 7: throw new Error('Invalid entity type');
                    case 8: return [2 /*return*/, Review.parse(response)];
                    case 9:
                        e_2 = _a.sent();
                        this._errorHandler.handleError(e_2);
                        throw e_2;
                    case 10: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * updates specified entity via api
     * @param {number} reviewId the id of the review to update.
     * @param {Review} review object containing rating and review information to save to ratings for this entity.
     * @param {IEntity} entity object containing summary information about this entity (airport or business).
     * @return {Promise<Review>} Resulting review record from API.
     */
    UserService.prototype.putReview = function (reviewId, review, entity) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, response, BODY, e_3;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to save a rating.');
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 9, , 10]);
                        response = void 0;
                        BODY = this._getReviewJSON(review);
                        if (!(entity.type === 'Airport')) return [3 /*break*/, 3];
                        return [4 /*yield*/, this._http.put(environment.airportsApiUrl + "/airports/" + entity.id + "/review/" + reviewId, BODY).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [3 /*break*/, 8];
                    case 3:
                        if (!(entity.type === 'Business')) return [3 /*break*/, 5];
                        return [4 /*yield*/, this._http.put(environment.airportsApiUrl + "/businesses/" + entity.id + "/review/" + reviewId, BODY).toPromise()];
                    case 4:
                        response = _a.sent();
                        return [3 /*break*/, 8];
                    case 5:
                        if (!(entity.type === 'Flying Club')) return [3 /*break*/, 7];
                        return [4 /*yield*/, this._http.put(environment.airportsApiUrl + "/businesses/" + entity.id + "/review/" + reviewId, BODY).toPromise()];
                    case 6:
                        response = _a.sent();
                        return [3 /*break*/, 8];
                    case 7: throw new Error('Invalid entity type');
                    case 8: return [2 /*return*/, Review.parse(response)];
                    case 9:
                        e_3 = _a.sent();
                        this._errorHandler.handleError(e_3);
                        throw e_3;
                    case 10: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * converts entity to json obj
     * @param {Review} review
     * @return {any}
     * @private
     */
    UserService.prototype._getReviewJSON = function (review) {
        return {
            title: review.title,
            review: review.review,
            rating: review.rating,
            subRatings: review.subRatings
        };
    };
    /**
     * updates specified entity via api
     * @param {number} reviewId the id of the review to update.
     * @param {number} photoId object containing a photo for this review.
     * @param {IEntity} entity object containing summary information about this entity (airport or business).
     * @return {Promise<Photo>} Resulting photo record from API.
     */
    UserService.prototype.putReviewPhoto = function (reviewId, photoId, entity) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, response, BODY, e_4;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to add a photo to a review.');
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 7, , 8]);
                        response = void 0;
                        BODY = this._getReviewPhotoJSON(reviewId, photoId, entity);
                        if (!(entity.type === 'Airport')) return [3 /*break*/, 3];
                        return [4 /*yield*/, this._http.put(environment.airportsApiUrl + "/photos/airport/update/" + photoId, BODY).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [3 /*break*/, 6];
                    case 3:
                        if (!(entity.type === 'Business')) return [3 /*break*/, 5];
                        return [4 /*yield*/, this._http.put(environment.airportsApiUrl + "/photos/business/update/" + photoId, BODY).toPromise()];
                    case 4:
                        response = _a.sent();
                        return [3 /*break*/, 6];
                    case 5: throw new Error('Invalid entity type');
                    case 6: return [2 /*return*/, Photo.parse(response)];
                    case 7:
                        e_4 = _a.sent();
                        this._errorHandler.handleError(e_4);
                        throw e_4;
                    case 8: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * converts entity to json obj
     * @param {number} reviewId
     * @param {number} photoId
     * @param {IEntity} entity
     * @return {any}
     * @private
     */
    UserService.prototype._getReviewPhotoJSON = function (reviewId, photoId, entity) {
        return {
            photoId: photoId,
            entityId: entity.id,
            reviewId: reviewId
        };
    };
    /**
     * Gets a user review for an entity (airport or business). Requires that the user is authenticated,
     * and that userInfo is loaded.
     * @param {IEntity} entity object containing summary information about this entity (airport or business).
     * @returns {Promise<Review>} User's review for entity from API.
     */
    UserService.prototype.getUserReview = function (entity) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to save a rating.');
                        }
                        if (!(entity.type === 'Airport')) return [3 /*break*/, 2];
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/airports/" + entity.id + "/reviews").toPromise()];
                    case 1:
                        response = _a.sent();
                        return [3 /*break*/, 7];
                    case 2:
                        if (!(entity.type === 'Business')) return [3 /*break*/, 4];
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/businesses/" + entity.id + "/reviews").toPromise()];
                    case 3:
                        response = _a.sent();
                        return [3 /*break*/, 7];
                    case 4:
                        if (!(entity.type === 'Flying Club')) return [3 /*break*/, 6];
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/businesses/" + entity.id + "/reviews").toPromise()];
                    case 5:
                        response = _a.sent();
                        return [3 /*break*/, 7];
                    case 6: throw new Error('Invalid entity type');
                    case 7:
                        if (response.review) {
                            return [2 /*return*/, Review.parse(response.review)];
                        }
                        return [2 /*return*/, null];
                }
            });
        });
    };
    /**
     * Gets the review summary for an entity (airport or business).
     * @param {IEntity} entity object containing summary information about this entity (airport or business).
     * @returns {Promise<ReviewSummary>} review summary for entity from API.
     */
    UserService.prototype.getReviewSummary = function (entity) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(entity.type === 'Airport')) return [3 /*break*/, 2];
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/airports/" + entity.id + "/reviews").toPromise()];
                    case 1:
                        response = _a.sent();
                        return [3 /*break*/, 7];
                    case 2:
                        if (!(entity.type === 'Business')) return [3 /*break*/, 4];
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/businesses/" + entity.id + "/reviews").toPromise()];
                    case 3:
                        response = _a.sent();
                        return [3 /*break*/, 7];
                    case 4:
                        if (!(entity.type === 'Flying Club')) return [3 /*break*/, 6];
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/businesses/" + entity.id + "/reviews").toPromise()];
                    case 5:
                        response = _a.sent();
                        return [3 /*break*/, 7];
                    case 6: throw new Error('Invalid entity type');
                    case 7: return [2 /*return*/, ReviewSummary.parse(response.reviewSummary)];
                }
            });
        });
    };
    /**
     * Sets the given airport identifier to the user's home airport. Requires that the user is authenticated,
     * and that userInfo is loaded.
     * @param {string} identifier Home identifier to set.
     * @returns {Promise<string>} Home airport record.
     */
    UserService.prototype.setHomeAirport = function (identifier) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
                        }
                        if (!identifier) {
                            throw new Error('Missing identifier');
                        }
                        // Check if home already exists
                        if (identifier === userInfo.homeAirportIdent) {
                            return [2 /*return*/, identifier];
                        }
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/customer/homeairport/" + identifier, null).toPromise()];
                    case 1:
                        response = _a.sent();
                        // Update userInfo
                        userInfo.homeAirportIdent = identifier;
                        this.userInfo.next(userInfo);
                        return [2 /*return*/, identifier];
                }
            });
        });
    };
    /**
     * Sets an interaction for this comment (thumb up or down, offensive. Requires that the user is authenticated,
     * and that userInfo is loaded.
     * @param {number} airportId airport.
     * @param {number} reviewId review id.
     * @param {string} interactionType The interaction to save {Offensive,ClearOffensive,ClearVote,UpVote,DownVote}.
     * @returns {Promise<void>} Nothing.
     */
    UserService.prototype.setAirportInteraction = function (airportId, reviewId, interactionType) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, newInteraction, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
                        }
                        if (!airportId || !reviewId || !interactionType) {
                            throw new Error('Missing information');
                        }
                        newInteraction = {
                            Interaction: interactionType
                        };
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/airports/" + airportId + "/review/" + reviewId + "/interaction", newInteraction).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response];
                }
            });
        });
    };
    /**
     * Sets an interaction for this comment (thumb up or down, offensive. Requires that the user is authenticated,
     * and that userInfo is loaded.
     * @param {number} businessId business.
     * @param {number} reviewId review id.
     * @param {string} interactionType The interaction to save {Offensive,ClearOffensive,ClearVote,UpVote,DownVote}.
     * @returns {Promise<void>} Nothing.
     */
    UserService.prototype.setBusinessInteraction = function (businessId, reviewId, interactionType) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, newInteraction, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
                        }
                        if (!businessId || !reviewId || !interactionType) {
                            throw new Error('Missing information');
                        }
                        newInteraction = {
                            Interaction: interactionType
                        };
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/businesses/" + businessId + "/review/" + reviewId + "/interaction", newInteraction).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response];
                }
            });
        });
    };
    /**
     * Sets an interaction for this aviation event
     * @param {number} aviationEventId aviation event
     * @param {string} interactionType The interaction to save {Offensive,ClearOffensive,ClearVote}.
     * @returns {Promise<void>} Nothing.
     */
    UserService.prototype.setAviationEventInteraction = function (aviationEventId, interactionType) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, newInteraction, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to add an interaction.');
                        }
                        if (!aviationEventId || !interactionType) {
                            throw new Error('Missing information');
                        }
                        newInteraction = {
                            Interaction: interactionType
                        };
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/events/" + aviationEventId + "/interaction", newInteraction).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response];
                }
            });
        });
    };
    /**
     * Sets an interaction for this comment (thumb up or down, offensive. Requires that the user is authenticated,
     * and that userInfo is loaded.
     * @param {number} airportId airport.
     * @param {number} reviewId review id.
     * @returns {Promise<void>} Nothing.
     */
    UserService.prototype.deleteAirportReview = function (airportId, reviewId) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
                        }
                        if (!airportId || !reviewId) {
                            throw new Error('Missing information');
                        }
                        return [4 /*yield*/, this._http.delete(environment.airportsApiUrl + "/airports/" + airportId + "/review/" + reviewId).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response];
                }
            });
        });
    };
    /**
     * Sets an interaction for this comment (thumb up or down, offensive. Requires that the user is authenticated,
     * and that userInfo is loaded.
     * @param {number} businessId business.
     * @param {number} reviewId review id.
     * @returns {Promise<void>} Nothing.
     */
    UserService.prototype.deleteBusinessReview = function (businessId, reviewId) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
                        }
                        if (!businessId || !reviewId) {
                            throw new Error('Missing information');
                        }
                        return [4 /*yield*/, this._http.delete(environment.airportsApiUrl + "/businesses/" + businessId + "/review/" + reviewId).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response];
                }
            });
        });
    };
    /**
     * Moves a review. Requires that the user is authenticated, and that userInfo is loaded.
     * @param {string} fromType either airport or business
     * @param {number} fromId the entities Id the review is being removed from
     * @param {string} toType either airport or business
     * @param {number} toId the entities Id the review is being removed to
     * @param {number} reviewId the Id of the review
     * @returns {Promise<void>} Nothing.
     */
    UserService.prototype.moveReview = function (fromType, fromId, toType, toId, reviewId) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, BODY, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
                        }
                        if (!fromType || !fromId || !toType || !toId || !reviewId) {
                            throw new Error('Missing information');
                        }
                        BODY = {
                            reviewId: reviewId,
                            fromType: fromType,
                            fromId: fromId,
                            toType: toType,
                            toId: toId
                        };
                        return [4 /*yield*/, this._http.put(environment.airportsApiUrl + "/reviews/move", BODY).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response];
                }
            });
        });
    };
    /**
     * Deletes a photo associated with a review
     * @param {number} photoId the id for an airport photo
     * @param {string} entityType type of entity to remove the photo from
     * @returns {Promise<void>} Nothing.
     */
    UserService.prototype.deleteReviewPhoto = function (photoId, entityType) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, entityAPI, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        entityAPI = 'business';
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to remove a review photo.');
                        }
                        if (!photoId || !entityType) {
                            throw new Error('Missing information');
                        }
                        if (entityType === 'Airport') {
                            entityAPI = 'airport';
                        }
                        return [4 /*yield*/, this._http.delete(environment.airportsApiUrl + "/photos/" + entityAPI + "/delete/" + photoId).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response];
                }
            });
        });
    };
    /**
     * Removes the given airport identifier as the user's home airport. Requires that the user is
     * authenticated, and that userInfo is loaded.
     * @param {string} identifier Airport identifier to remove.
     * @returns {Promise<void>}
     */
    UserService.prototype.removeHomeAirport = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
                        }
                        // Remove the favorite
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/customer/homeairport/", null).toPromise()];
                    case 1:
                        // Remove the favorite
                        _a.sent();
                        // Update userInfo
                        userInfo.homeAirportIdent = '';
                        this.userInfo.next(userInfo);
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Sets the given airport identifier to the user's home airport. Requires that the user is authenticated,
     * and that userInfo is loaded.
     * @param {string} identifier Home identifier to set.
     * @returns {Promise<string>} Home airport record.
     */
    UserService.prototype.setAircraftType = function (aircraftType) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to set a default aircraft type.');
                        }
                        if (!aircraftType) {
                            throw new Error('Missing aircraft type.');
                        }
                        // Check if home already exists
                        if (aircraftType === userInfo.aircraftType) {
                            return [2 /*return*/, aircraftType];
                        }
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/customer/aircraftType/" + aircraftType, null).toPromise()];
                    case 1:
                        response = _a.sent();
                        // Update userInfo
                        userInfo.aircraftType = aircraftType;
                        this.userInfo.next(userInfo);
                        return [2 /*return*/, aircraftType];
                }
            });
        });
    };
    /**
     * Gets the authenticated user's UserEcho token. Note this method requires that the user is authenticated.
     * @returns {Promise<string>} SSO token
     */
    UserService.prototype.getUserechoInfo = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, userEcho, e_5;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser) {
                            return [2 /*return*/, null];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.get("https://webservice.aopa.org/userecho/userinfo.cfm", { withCredentials: true }).toPromise()];
                    case 2:
                        response = _a.sent();
                        userEcho = UserEcho.parse(response);
                        return [2 /*return*/, userEcho.userEchoToken];
                    case 3:
                        e_5 = _a.sent();
                        this._errorHandler.handleError(e_5);
                        throw e_5;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    UserService.prototype.getWhatsNewVersion = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, URL, response, whatsNewObject, e_6;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            return [2 /*return*/, null];
                        }
                        URL = environment.airportsApiUrl + "/customer/userData";
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.get(URL).toPromise()];
                    case 2:
                        response = _a.sent();
                        whatsNewObject = response.find(function (obj) {
                            return obj.dataPointId === 3007;
                        });
                        if (whatsNewObject) {
                            return [2 /*return*/, whatsNewObject.dataValue];
                        }
                        return [2 /*return*/, null];
                    case 3:
                        e_6 = _a.sent();
                        this._errorHandler.handleError(e_6);
                        return [2 /*return*/, null];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    UserService.prototype.saveWhatsNewVersion = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, data, URL, RESPONSE, e_7;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            return [2 /*return*/];
                        }
                        data = {
                            dataName: 'WhatsNewVersion',
                            dataValue: environment.version
                        };
                        URL = environment.airportsApiUrl + "/customer/userData";
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.post(URL, data).toPromise()];
                    case 2:
                        RESPONSE = _a.sent();
                        return [2 /*return*/];
                    case 3:
                        e_7 = _a.sent();
                        this._errorHandler.handleError(e_7);
                        return [2 /*return*/];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Adds the given event id to the user's list of favorite events. Requires that the user is authenticated,
     * and that userInfo is loaded.
     * @param {number} eventId event id to add.
     * @returns {Promise<FavoriteAirport>} Favorite event record.
     */
    UserService.prototype.addFavoriteEvent = function (eventId) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, existingFavorite, response, favoriteEvent;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available in order to add a favorite event.');
                        }
                        if (!eventId) {
                            throw new Error('Missing event id');
                        }
                        existingFavorite = userInfo.favoriteEvents.find(function (f) { return f.eventId === eventId; });
                        if (existingFavorite) {
                            return [2 /*return*/, existingFavorite];
                        }
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/customer/favorites/event/" + eventId, null).toPromise()];
                    case 1:
                        response = _a.sent();
                        favoriteEvent = FavoriteEvent.parse(response);
                        // Update userInfo
                        userInfo.favoriteEvents.push(favoriteEvent);
                        this.userInfo.next(userInfo);
                        return [2 /*return*/, favoriteEvent];
                }
            });
        });
    };
    /**
     * Removes the given event id from the user's list of favorite events. Requires that the user is
     * authenticated, and that userInfo is loaded.
     * @param {number} eventId event id to remove.
     * @returns {Promise<void>}
     */
    UserService.prototype.removeFavoriteEvent = function (eventId) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, index, existingFavorite;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            throw new Error('User must be authenticated and user info must be available to remove a favorite event.');
                        }
                        if (!eventId) {
                            throw new Error('Missing identifier');
                        }
                        index = userInfo.favoriteEvents.findIndex(function (f) { return f.eventId === eventId; });
                        existingFavorite = null;
                        if (index >= 0) {
                            existingFavorite = userInfo.favoriteEvents[index];
                        }
                        else {
                            return [2 /*return*/];
                        }
                        // Remove the favorite
                        return [4 /*yield*/, this._http.delete(environment.airportsApiUrl + "/customer/favorites/" + existingFavorite.userDataId).toPromise()];
                    case 1:
                        // Remove the favorite
                        _a.sent();
                        // Update userInfo
                        userInfo.favoriteEvents.splice(index, 1);
                        this.userInfo.next(userInfo);
                        return [2 /*return*/];
                }
            });
        });
    };
    UserService.prototype.getShowAirportsFlag = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, URL, response, showAirportsObject, e_8;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            return [2 /*return*/, null];
                        }
                        URL = environment.airportsApiUrl + "/customer/userData";
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.get(URL).toPromise()];
                    case 2:
                        response = _a.sent();
                        showAirportsObject = response.find(function (obj) {
                            return obj.dataPointId === 6001;
                        });
                        if (showAirportsObject) {
                            if (showAirportsObject.dataValue === 'true' || showAirportsObject.dataValue === '1') {
                                return [2 /*return*/, true];
                            }
                            else {
                                return [2 /*return*/, false];
                            }
                        }
                        return [2 /*return*/, null];
                    case 3:
                        e_8 = _a.sent();
                        this._errorHandler.handleError(e_8);
                        return [2 /*return*/, null];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    UserService.prototype.saveShowAirportsFlag = function (newValue) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, stringValue, data, URL, RESPONSE, e_9;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            return [2 /*return*/];
                        }
                        if (newValue) {
                            stringValue = '1';
                        }
                        else {
                            stringValue = '0';
                        }
                        data = {
                            dataName: 'ShowAirportsFlag',
                            dataValue: stringValue
                        };
                        URL = environment.airportsApiUrl + "/customer/userData";
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.post(URL, data).toPromise()];
                    case 2:
                        RESPONSE = _a.sent();
                        return [2 /*return*/];
                    case 3:
                        e_9 = _a.sent();
                        this._errorHandler.handleError(e_9);
                        return [2 /*return*/];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    UserService.prototype.saveAcceptedTermsAndPrivacy = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userInfo, data, URL, RESPONSE, e_10;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userInfo = this.userInfo.getValue();
                        if (!userInfo) {
                            return [2 /*return*/];
                        }
                        data = {
                            dataName: 'AcceptedTerms',
                            dataValue: '1'
                        };
                        URL = environment.airportsApiUrl + "/customer/userData";
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.post(URL, data).toPromise()];
                    case 2:
                        RESPONSE = _a.sent();
                        return [2 /*return*/];
                    case 3:
                        e_10 = _a.sent();
                        this._errorHandler.handleError(e_10);
                        return [2 /*return*/];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    return UserService;
}());
export { UserService };
